import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function PhenomenaBasedLearningPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
      isActive: true,
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Phenomena-based learning"
        description="Students explore a real-world phenomenon and are motivated to build the disciplinary knowledge to understand it. Curiosity, not the textbook table of contents, drives the learning sequence and keeps content tightly connected to authentic contexts."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'Finnish educational psychologist Prof. Maijaliisa Rauste-von Wright in the mid-1990s.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                A middle‑school randomized control trial showed a large gain in metacognitive
                awareness—being consciously aware of how you learn and deliberately using that
                knowledge to adjust your learning in the moment—compared with traditional lessons
                (Cohen’s d = 2.06;{' '}
                <Link url="https://eric.ed.gov/?id=EJ1296934" openInNewTab>
                  Akkaş & Eker, 2021
                </Link>
                ).
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link
                url="https://link.springer.com/chapter/10.1007/978-3-319-78580-6_13"
                openInNewTab
              >
                Finnish National Core Curriculum 2016.
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Every Stile unit is centered around an intriguing phenomenon or engineering problem, intentionally selected to motivate students’ curiosity. Upon their introduction to the anchoring phenomenon, students are driven to ask questions and seek answers that explain it.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="how-global-communication-works"
            title="How global communication works"
            description="The Waves unit is driven by the question: “How can someone on the other side of the world see and hear you?”"
            url="http://stileapp.com/go/phenomena-example-1"
            subtitle={
              <div>
                <em>Shining a light on communication</em> lesson
                <br />
                in the Waves unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/phenomena_global_comm.png"
                alt="Text: How do you communicate? Image: Illustrations of students using phones and digital communication, with supporting text about how communication has changed with technology."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="minimizing-the-impact-of-mining-lithium"
            title="Minimizing the impact of mining lithium"
            description="The Elements and Compounds unit is driven by the question: “If we need to mine lithium, how do we minimize the impact?”"
            url="http://stileapp.com/go/phenomena-example-2"
            subtitle={
              <div>
                <em>Should we keep mining lithium?</em> lesson
                <br />
                in the Elements and Compounds – Lithium Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/phenomena_lithium.png"
                alt="Text: If we need to mine lithium, how do we minimize the impact? Image: Illustration of a mining truck, wind turbines, electric cars, and animals near a mining site with a cartoon electric car character."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="what-dinosaur-skeletons-can-teach-us"
            title="What dinosaur skeletons can teach us"
            description="Students investigate a recently-discovered dinosaur skeleton to learn about fossil formation."
            url="http://stileapp.com/go/phenomena-example-3"
            subtitle={
              <div>
                <em>The fossil record</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/phenomena_fossil.png"
                alt="Map of Australia highlighting Eromanga in Queensland with a photo of a dinosaur fossil dig site and a reconstructed sauropod model."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default PhenomenaBasedLearningPage;
